import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import {
  baseAssignmentsActions,
  BaseAvailableFileTypeEnum,
  BaseCoreState,
  BaseInquiryService,
  BaseModalService,
  baseOffersActions,
  baseOffersSelectors,
  BaseRolesService,
  WorkCouncilStatus,
  BaseModalHeaderSizes,
} from 'base';
import { BaseCustomerWorkCouncilStatusEnum } from '../../_enums/base-customer-work-council-status.enum';

@Component({
  selector: 'base-customer-employee-work-council-tab',
  templateUrl: './base-customer-employee-work-council-tab.component.html',
})
export class BaseCustomerEmployeeWorkCouncilTabComponent implements OnInit, OnDestroy {
  @ViewChild('changeStatusConfirmModal') changeStatusConfirmModal: ElementRef;
  @ViewChild('documentModalBody') documentModalBody: ElementRef;
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;

  @Input() offerId!: number;
  @Input() assignmentId?: number;
  @Input() inquiryId?: number;
  @Input() offeredDaysTotalAmount: number;
  @Input() profileFullName: string;
  @Input() workCouncilStatus?: WorkCouncilStatus;

  approveDisapproveModalRef: NgbModalRef;
  baseAvailableFileTypeEnum = BaseAvailableFileTypeEnum;
  selectedStatus: BaseCustomerWorkCouncilStatusEnum | undefined;
  documentViewerModalRef: NgbModalRef;
  activeDocumentSrc: Blob;
  documentViewerBodyText: string;
  documentViewerBodyTextTranslateParams: { amountOfDays?: number } = {};
  isWorkCouncil = false;

  private destroy$ = new Subject();

  constructor(
    public baseInquiryService: BaseInquiryService,
    private modalService: BaseModalService,
    private store: Store<BaseCoreState>,
    private baseRoleService: BaseRolesService
  ) {}

  ngOnInit() {
    this.isWorkCouncil = this.baseRoleService.hasWorkCouncilRole();
    this.loadWorkCouncilCommentList();
    this.downloadedFileSubscription();
    this.setDocumentViewerBodyText();
  }

  ngOnDestroy() {
    this.store.dispatch(baseOffersActions.clearDownloadOfferedDaysFile());
    this.destroy$.next(true);
  }

  onStatusChange(status: BaseCustomerWorkCouncilStatusEnum) {
    this.openApproveDisapproveConfirmationModal(status);
  }

  openDocumentViewer(document: Blob): void {
    this.activeDocumentSrc = document;
    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelText: 'action.close',
        cancelButtonOnly: true,
        okButtonDisplayed: false,
        hideHeader: true,
      },
      { size: 'lg', backdrop: 'static' }
    );

    this.documentViewerModalRef.result.then(noop, noop);
  }

  downloadOfferedDaysFile(): void {
    this.store.dispatch(
      baseOffersActions.downloadOfferedDaysFile({ payload: { offer: this.offerId, openInModal: true } })
    );
  }

  private openApproveDisapproveConfirmationModal(status: BaseCustomerWorkCouncilStatusEnum) {
    this.selectedStatus = status;
    this.approveDisapproveModalRef = this.modalService.open(
      {
        headText: `workCouncil.confirmationModal.title.${status}`,
        headerSize: BaseModalHeaderSizes.lg,
        bodyTemplate: this.changeStatusConfirmModal,
        scrollToTop: true,
        hideFooter: true,
      },
      {
        scrollable: true,
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.approveDisapproveModalRef.result.then((comment: string) => {
      switch (status) {
        case BaseCustomerWorkCouncilStatusEnum.APPROVED:
          this.approveOffer(this.offerId, this.assignmentId, comment || undefined, this.inquiryId);
          break;
        case BaseCustomerWorkCouncilStatusEnum.DISAPPROVED:
          this.disapproveOffer(this.offerId, comment, this.assignmentId, this.inquiryId);
          break;
        case BaseCustomerWorkCouncilStatusEnum.NOTED:
          this.noteOffer(this.offerId, comment, this.assignmentId, this.inquiryId);
          break;
      }
    }, noop);
  }

  private approveOffer(offerId: number, assignmentId?: number, comment?: string, inquiryId?: number) {
    this.store.dispatch(
      baseAssignmentsActions.approveInquiryOffer({
        payload: {
          offer: offerId,
          body: {
            approvementReason: comment,
          },
          assignmentId,
          inquiryId,
        },
      })
    );
  }

  private disapproveOffer(offerId: number, reason: string, assignmentId?: number, inquiryId?: number) {
    this.store.dispatch(
      baseAssignmentsActions.disapproveInquiryOffer({
        payload: {
          offer: offerId,
          body: {
            approvementReason: reason,
          },
          assignmentId,
          inquiryId,
        },
      })
    );
  }

  private noteOffer(offerId: number, comment: string, assignmentId?: number, inquiryId?: number) {
    this.store.dispatch(
      baseAssignmentsActions.noteInquiryOffer({
        payload: {
          offer: offerId,
          body: {
            note: comment,
          },
          assignmentId,
          inquiryId,
        },
      })
    );
  }

  private loadWorkCouncilCommentList(): void {
    this.store.dispatch(
      baseAssignmentsActions.loadWorkCouncilCommentList({
        payload: {
          offer: this.offerId,
        },
      })
    );
  }

  private downloadedFileSubscription(): void {
    this.store
      .select(baseOffersSelectors.getDownloadedFile)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data),
        map((state) => state.data)
      )
      .subscribe((document) => {
        this.openDocumentViewer(document as Blob);
      });
  }

  private setDocumentViewerBodyText(): void {
    let documentViewerBodyTextSuffix = 'shortOffer';

    if (this.offeredDaysTotalAmount === undefined) {
      documentViewerBodyTextSuffix = 'employeeModal';
    } else if (this.offeredDaysTotalAmount > 28) {
      documentViewerBodyTextSuffix = 'longOffer';
    }

    this.documentViewerBodyText = `workCouncil.offers.offeredDaysPreviewText.${documentViewerBodyTextSuffix}`;

    if (this.offeredDaysTotalAmount !== undefined) {
      this.documentViewerBodyTextTranslateParams = { amountOfDays: this.offeredDaysTotalAmount };
    }
  }
}
